/**
 * NOXCULT — Drop Room v5
 * drop-room.js
 */
(function () {
  'use strict';

  class LoreOverlay {
    constructor(roomEl, stageEl) {
      this.roomEl = roomEl;
      this.stageEl = stageEl || roomEl;
      this.el = document.getElementById('nox-lore');
      this.textEl = document.getElementById('nox-lore-text');
      this._active = null;
      this.positionMode = roomEl?.dataset.lorePosition || 'auto';
      this.animationMode = roomEl?.dataset.loreAnimation || 'fade-up';
    }
    open(hs) {
      if (!this.el || !this.textEl || !this.roomEl || !hs) return false;
      this.positionMode = this.roomEl.dataset.lorePosition || 'auto';
      this.animationMode = this.roomEl.dataset.loreAnimation || 'fade-up';
      const items = this._buildItems(hs.dataset.hsContent || '');
      if (!items.length) {
        this.close();
        return false;
      }
      if (this._active && this._active !== hs) this._active.setAttribute('aria-expanded','false');
      this._active = hs;
      hs.setAttribute('aria-expanded','true');
      this.el.dataset.anim = this.animationMode;
      this._render(items);
      this._position(hs);
      this.el.setAttribute('aria-label', hs.dataset.hsLabel || 'Detail');
      this.el.setAttribute('aria-hidden', 'false');
      this.el.classList.add('is-open');
      return true;
    }
    refreshPosition() {
      if (this._active) this._position(this._active);
    }
    _buildItems(content) {
      const rows = String(content || '').replace(/\r\n?/g, '\n').split('\n');
      while (rows.length && !rows[0].trim()) rows.shift();
      while (rows.length && !rows[rows.length - 1].trim()) rows.pop();
      const items = [];
      let lastWasGap = false;
      rows.forEach(row => {
        const text = row.trim();
        if (!text) {
          if (!lastWasGap) items.push({ type: 'gap' });
          lastWasGap = true;
          return;
        }
        items.push({ type: 'line', text });
        lastWasGap = false;
      });
      return items;
    }
    _render(items) {
      this.textEl.textContent = '';
      items.forEach((item, index) => {
        const node = document.createElement('span');
        node.style.setProperty('--line-index', String(index));
        if (item.type === 'gap') {
          node.className = 'nox-lore__gap';
          node.setAttribute('aria-hidden', 'true');
        } else {
          node.className = 'nox-lore__line';
          node.textContent = item.text;
        }
        this.textEl.appendChild(node);
      });
    }
    _position(hs) {
      const roomRect = (this.stageEl || this.roomEl).getBoundingClientRect();
      const hsRect = hs.getBoundingClientRect();
      const centerX = (hsRect.left - roomRect.left) + (hsRect.width / 2);
      const centerY = (hsRect.top - roomRect.top) + (hsRect.height / 2);
      const compact = roomRect.width < 680;
      const baseOffset = parseFloat(getComputedStyle(this.roomEl).getPropertyValue('--lore-offset')) || 32;
      const horizontalOffset = compact ? Math.max(18, baseOffset * 0.72) : baseOffset;
      const verticalOffset = compact ? Math.max(20, baseOffset * 0.82) : baseOffset;
      const mode = this.positionMode || 'auto';
      let left = centerX;
      let top = centerY;
      let anchorX = '-50%';
      let anchorY = '-100%';
      let align = 'center';

      const applyPosition = (x, y, ax, ay, textAlign) => {
        this.el.style.setProperty('--lore-x', `${Math.round(x)}px`);
        this.el.style.setProperty('--lore-y', `${Math.round(y)}px`);
        this.el.style.setProperty('--lore-anchor-x', ax);
        this.el.style.setProperty('--lore-anchor-y', ay);
        this.el.dataset.align = textAlign;
      };

      if (mode !== 'auto') {
        const pad = compact ? 18 : 28;
        switch (mode) {
          case 'right':
            applyPosition(centerX + horizontalOffset, centerY, '0%', '-50%', 'left');
            return;
          case 'left':
            applyPosition(centerX - horizontalOffset, centerY, '-100%', '-50%', 'right');
            return;
          case 'above':
            applyPosition(centerX, centerY - verticalOffset, '-50%', '-100%', 'center');
            return;
          case 'below':
            applyPosition(centerX, centerY + verticalOffset, '-50%', '0%', 'center');
            return;
          case 'center':
            applyPosition(roomRect.width / 2, roomRect.height / 2, '-50%', '-50%', 'center');
            return;
          case 'top-left':
            applyPosition(pad, pad, '0%', '0%', 'left');
            return;
          case 'top-right':
            applyPosition(roomRect.width - pad, pad, '-100%', '0%', 'right');
            return;
          case 'bottom-left':
            applyPosition(pad, roomRect.height - pad, '0%', '-100%', 'left');
            return;
          case 'bottom-right':
            applyPosition(roomRect.width - pad, roomRect.height - pad, '-100%', '-100%', 'right');
            return;
          default:
            break;
        }
      }

      if (compact) {
        if (centerX <= roomRect.width * 0.5) {
          left = centerX + horizontalOffset;
          anchorX = '0%';
          align = 'left';
        } else {
          left = centerX - horizontalOffset;
          anchorX = '-100%';
          align = 'right';
        }
      } else if (centerX < roomRect.width * 0.34) {
        left = centerX + horizontalOffset;
        anchorX = '0%';
        align = 'left';
      } else if (centerX > roomRect.width * 0.66) {
        left = centerX - horizontalOffset;
        anchorX = '-100%';
        align = 'right';
      }

      if (centerY < roomRect.height * 0.34) {
        top = centerY + verticalOffset;
        anchorY = '0%';
      } else {
        top = centerY - verticalOffset;
        anchorY = '-100%';
      }

      applyPosition(left, top, anchorX, anchorY, align);
    }
    close() {
      if (!this.el) return;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      if (this._active) { this._active.setAttribute('aria-expanded','false'); this._active=null; }
    }
    isOpen() { return this.el?.classList.contains('is-open')??false; }
  }

  class VariantSync {
    constructor(handle, cbs) {
      this._data=null; this._cbs=cbs||{};
      if (!handle) return;
      fetch(`/products/${handle}.js`)
        .then(r=>{if(!r.ok)throw r;return r.json();})
        .then(d=>{this._data=d;})
        .catch(e=>console.warn('[NOX] variants:',e));
    }
    onRadioChange(panelEl) {
      if (!this._data) return;
      const radios  = [...panelEl.querySelectorAll('.nox-panel__radio:checked')];
      const options = radios.sort((a,b)=>+a.dataset.optionPos-+b.dataset.optionPos).map(r=>r.value);
      const v = this._data.variants.find(v=>{
        const opts=[v.option1,v.option2,v.option3].filter(Boolean);
        return options.every((o,i)=>opts[i]===o);
      });
      if (v) this._cbs.onVariantResolved?.(v);
    }
    fmt(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
      return (cents/100).toLocaleString('fr-FR',{style:'currency',currency:'EUR'});
    }
  }

  class ProductPanel {
    constructor(roomEl, vs, audio) {
      this.roomEl   = roomEl; this.vs=vs; this.audio=audio;
      this.panel    = document.getElementById('nox-product-panel');
      this.cta      = document.getElementById('nox-room-cta');
      this.ctaMode  = this.cta?.dataset.ctaMode || 'panel';
      this.backdrop = document.getElementById('nox-room-backdrop');
      this.form     = document.getElementById('nox-panel-form');
      this.varInput = document.getElementById('nox-panel-variant-id');
      this.stockEl  = document.getElementById('nox-panel-stock');
      this.atcBtn   = document.getElementById('nox-panel-atc');
      this.atcLabel = document.getElementById('nox-panel-atc-label');
      this.roomPrice= document.getElementById('nox-room-price');
      if (!this.panel) return;
      this._bind();
    }
    _bind() {
      if (this.ctaMode === 'panel') {
        this.cta?.addEventListener('click', e=>{
          e.preventDefault();
          this.open();
        });
      }
      document.getElementById('nox-panel-close')?.addEventListener('click',()=>this.close());
      this.backdrop?.addEventListener('click',()=>this.close());
      this.panel.addEventListener('change',e=>{
        if (e.target.classList.contains('nox-panel__radio')) this.vs.onRadioChange(this.panel);
      });
      this.vs._cbs.onVariantResolved = v=>this._applyVariant(v);
      this.form?.addEventListener('submit',e=>{e.preventDefault();this._doATC();});
      this.atcBtn?.addEventListener('click',()=>this.form?.dispatchEvent(new Event('submit',{bubbles:true})));
    }
    _applyVariant(v) {
      if (this.varInput) this.varInput.value=v.id;
      const priceEl=document.getElementById('nox-panel-price');
      if (priceEl) {
        const cmp=v.compare_at_price>v.price?`<s class="nox-panel__price-compare">${this.vs.fmt(v.compare_at_price)}</s> `:'';
        priceEl.innerHTML=cmp+`<span class="nox-panel__price-now" id="nox-panel-price-now">${this.vs.fmt(v.price)}</span>`;
      }
      if (this.roomPrice) this.roomPrice.textContent=this.vs.fmt(v.price);
      if (this.stockEl) {
        let h;
        if (!v.available) h='<span class="nox-panel__stock--out">Épuisé</span>';
        else if (v.inventory_management==='shopify'&&v.inventory_quantity>0&&v.inventory_quantity<=5)
          h=`<span class="nox-panel__stock--low">Dernières pièces (${v.inventory_quantity})</span>`;
        else h='<span class="nox-panel__stock--ok">Disponible</span>';
        this.stockEl.innerHTML=h;
      }
      if (this.atcBtn) {
        this.atcBtn.disabled=!v.available;
        if (this.atcLabel) this.atcLabel.textContent=v.available?'Ajouter au panier':'Épuisé';
      }
    }
    _doATC() {
      if (!this.varInput||!this.atcBtn) return;
      this.atcBtn.classList.add('is-loading'); this.atcBtn.disabled=true;
      fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:this.varInput.value,quantity:1})})
        .then(r=>{if(!r.ok)return r.json().then(e=>Promise.reject(e));return r.json();})
        .then(()=>{
          this.atcBtn.classList.remove('is-loading'); this.atcBtn.disabled=false;
          if (this.atcLabel){this.atcLabel.textContent='Ajouté ✓';setTimeout(()=>{this.atcLabel.textContent='Ajouter au panier';},2200);}
          document.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true}));
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open',{bubbles:true}));
          fetch('/cart.js').then(r=>r.json()).then(c=>{document.querySelectorAll('[data-cart-count]').forEach(el=>el.textContent=c.item_count);}).catch(()=>{});
        })
        .catch(()=>{
          this.atcBtn.classList.remove('is-loading'); this.atcBtn.disabled=false;
          if (this.atcLabel){this.atcLabel.textContent='Erreur — Réessayer';setTimeout(()=>{this.atcLabel.textContent='Ajouter au panier';},2600);}
        });
    }
    open() {
      if (!this.panel) return;
      this.audio?.playSFX('open');
      this.panel.removeAttribute('inert'); this.panel.removeAttribute('aria-hidden');
      this.panel.classList.add('is-open'); this.roomEl.classList.add('panel-open');
      this.cta?.setAttribute('aria-expanded','true');
      requestAnimationFrame(()=>{const f=this.panel.querySelector('button:not(:disabled),[href],input:not(:disabled)');f?.focus({preventScroll:true});});
    }
    close() {
      if (!this.panel) return;
      this.audio?.playSFX('close');
      this.panel.setAttribute('inert',''); this.panel.setAttribute('aria-hidden','true');
      this.panel.classList.remove('is-open'); this.roomEl.classList.remove('panel-open');
      this.cta?.setAttribute('aria-expanded','false'); this.cta?.focus({preventScroll:true});
    }
    isOpen() { return this.panel?.classList.contains('is-open')??false; }
  }

  class Room {
    constructor(el) {
      this.el       = el;
      this.stage    = el.querySelector('[data-room-stage]');
      this.hotspots = el.querySelectorAll('.nox-hotspot');
      this.back     = el.querySelector('[data-back]');
      this.audioBtn = document.getElementById('nox-audio-toggle');
      this.cta      = document.getElementById('nox-room-cta');
      this._hoveredHotspot = null;
      this._activeHotspot = null;
      this._parallaxTarget = { x: 0, y: 0 };
      this._parallaxCurrent = { x: 0, y: 0 };
      this._parallaxFrame = 0;
      this._onKeydown = null;
      this._onDocumentClick = null;
      this._onResize = null;
      this._onPointerMove = null;
      this._onPointerLeave = null;
      const cfg     = window.__NOX__?.room || {};
      this.intensity = cfg.effectIntensity ?? 0.5;
      this.transition = cfg.transition || {};
      this._applyThemeFontVars();
      this._layoutStage();

      /* WebGL */
      this.atm = null;
      if ((cfg.enableWebGL??true) && window.NoxAtmosphere) {
        const canvas = document.getElementById('nox-room-webgl');
        if (canvas) this.atm = new NoxAtmosphere(canvas, { intensity: this.intensity, fps: 28 });
      }

      /* Audio */
      this.audio = null;
      if (cfg.audio && window.NoxAudio) {
        const a=cfg.audio;
        if (a.startMuted) sessionStorage.setItem('nox_audio_muted','1');
        const sfx={};
        ['hotspot','open','close','navigate'].forEach(k=>{ if(a.sfx?.[k]?.src) sfx[k]=a.sfx[k]; });
        this.audio = new NoxAudio({ autoplay:a.autoplay!==false, ambient:a.ambient?.src?a.ambient:null, sfx });
        this.audio.playAmbient();
      }

      const vs = new VariantSync(cfg.productHandle, {});
      this.lore  = new LoreOverlay(el, this.stage);
      this.panel = new ProductPanel(el, vs, this.audio);

      this._bindHotspots();
      this._bindBack();
      this._bindAudioBtn();
      this._bindCTA();
      this._bindKeyboard();
      this._bindDismiss();
      this._bindResize();
      this._bindParallax();
      this._reveal();
    }

    _applyThemeFontVars() {
      if (!this.el || !document.body) return;
      const bodyStyle = getComputedStyle(document.body);
      const bodyFamily = bodyStyle.fontFamily;

      const probe = document.createElement('h1');
      probe.textContent = 'A';
      probe.style.cssText = 'position:fixed;left:-9999px;top:-9999px;visibility:hidden;pointer-events:none;margin:0;padding:0;';
      document.body.appendChild(probe);
      const headingFamily = getComputedStyle(probe).fontFamily;
      probe.remove();

      if (bodyFamily) this.el.style.setProperty('--nox-theme-body-family', bodyFamily);
      if (headingFamily) this.el.style.setProperty('--nox-theme-heading-family', headingFamily);
    }

    _reveal() { setTimeout(()=>this.el.classList.add('is-ready'), 200); }

    _layoutStage() {
      if (!this.stage || !this.el) return;
      const roomRect = this.el.getBoundingClientRect();
      if (!roomRect.width || !roomRect.height) return;
      const styles = getComputedStyle(this.el);
      const ratioW = parseFloat(styles.getPropertyValue('--room-stage-ratio-w')) || 16;
      const ratioH = parseFloat(styles.getPropertyValue('--room-stage-ratio-h')) || 9;
      const ratio = ratioW / Math.max(ratioH, 1);
      const maxWidth = parseFloat(styles.getPropertyValue('--room-stage-max-w')) || roomRect.width;
      const padX = parseFloat(styles.getPropertyValue('--room-stage-pad-x')) || 0;
      const padY = parseFloat(styles.getPropertyValue('--room-stage-pad-y')) || 0;
      const availableWidth = Math.max(roomRect.width - (padX * 2), 0);
      const availableHeight = Math.max(roomRect.height - (padY * 2), 0);
      let stageWidth = Math.min(availableWidth, maxWidth);
      let stageHeight = stageWidth / Math.max(ratio, 0.01);
      if (stageHeight > availableHeight) {
        stageHeight = availableHeight;
        stageWidth = stageHeight * ratio;
      }
      this.stage.style.setProperty('--room-stage-width', `${Math.round(stageWidth)}px`);
      this.stage.style.setProperty('--room-stage-height', `${Math.round(stageHeight)}px`);
    }

    _queueParallax() {
      if (this._parallaxFrame) return;
      const tick = () => {
        this._parallaxCurrent.x += (this._parallaxTarget.x - this._parallaxCurrent.x) * 0.085;
        this._parallaxCurrent.y += (this._parallaxTarget.y - this._parallaxCurrent.y) * 0.085;
        this.el.style.setProperty('--parallax-x', `${this._parallaxCurrent.x.toFixed(2)}px`);
        this.el.style.setProperty('--parallax-y', `${this._parallaxCurrent.y.toFixed(2)}px`);

        const dx = Math.abs(this._parallaxTarget.x - this._parallaxCurrent.x);
        const dy = Math.abs(this._parallaxTarget.y - this._parallaxCurrent.y);
        if (dx < 0.05 && dy < 0.05 && Math.abs(this._parallaxTarget.x) < 0.05 && Math.abs(this._parallaxTarget.y) < 0.05) {
          this._parallaxCurrent.x = 0;
          this._parallaxCurrent.y = 0;
          this.el.style.setProperty('--parallax-x', '0px');
          this.el.style.setProperty('--parallax-y', '0px');
          this._parallaxFrame = 0;
          return;
        }
        this._parallaxFrame = window.requestAnimationFrame(tick);
      };
      this._parallaxFrame = window.requestAnimationFrame(tick);
    }

    _bindParallax() {
      if (!this.el || !this.stage) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const strength = parseFloat(getComputedStyle(this.el).getPropertyValue('--parallax-strength')) || 0;
      if (strength <= 0) return;

      this._onPointerMove = e => {
        const rect = this.el.getBoundingClientRect();
        const nx = (((e.clientX - rect.left) / Math.max(rect.width, 1)) - 0.5) * 2;
        const ny = (((e.clientY - rect.top) / Math.max(rect.height, 1)) - 0.5) * 2;
        this._parallaxTarget.x = nx * strength * -1;
        this._parallaxTarget.y = ny * strength * -1;
        this._queueParallax();
      };

      this._onPointerLeave = () => {
        this._parallaxTarget.x = 0;
        this._parallaxTarget.y = 0;
        this._queueParallax();
      };

      this.el.addEventListener('pointermove', this._onPointerMove);
      this.el.addEventListener('pointerleave', this._onPointerLeave);
    }

    _getHotspotFocus(hs) {
      const roomRect = this.el.getBoundingClientRect();
      const hsRect = hs.getBoundingClientRect();
      const x = (hsRect.left - roomRect.left + (hsRect.width / 2)) / Math.max(roomRect.width, 1);
      const y = (hsRect.top - roomRect.top + (hsRect.height / 2)) / Math.max(roomRect.height, 1);
      return {
        x: Math.max(0, Math.min(1, x)),
        y: Math.max(0, Math.min(1, y))
      };
    }

    _setAtmosphereState(mode, hs = null) {
      if (!this.atm) return;
      if (hs) {
        const focus = this._getHotspotFocus(hs);
        this.atm.setFocus(focus.x, focus.y);
      }
      this.atm.setMode(mode);
    }

    _syncHotspotState() {
      this.el.classList.toggle('is-hotspot-hovered', !!this._hoveredHotspot && !this._activeHotspot);
      this.el.classList.toggle('has-active-hotspot', !!this._activeHotspot);
    }

    _setHoveredHotspot(hs) {
      if (this.panel.isOpen()) return;
      this._hoveredHotspot = hs;
      this._syncHotspotState();
      if (!this._activeHotspot) this._setAtmosphereState('hover', hs);
    }

    _clearHoveredHotspot(hs) {
      if (hs && this._hoveredHotspot !== hs) return;
      this._hoveredHotspot = null;
      this._syncHotspotState();
      if (this._activeHotspot) this._setAtmosphereState('active', this._activeHotspot);
      else this._setAtmosphereState('idle');
    }

    _setActiveHotspot(hs) {
      this._activeHotspot = hs;
      this._syncHotspotState();
      if (hs) this._setAtmosphereState('active', hs);
      else if (this._hoveredHotspot) this._setAtmosphereState('hover', this._hoveredHotspot);
      else this._setAtmosphereState('idle');
    }

    _openLore(hs) {
      const opened = this.lore.open(hs);
      this._setActiveHotspot(opened ? hs : null);
      return opened;
    }

    _closeLore() {
      this.lore.close();
      this._setActiveHotspot(null);
    }

    _bindHotspots() {
      this.hotspots.forEach(hs=>{
        hs.addEventListener('mouseenter',()=>this._setHoveredHotspot(hs));
        hs.addEventListener('mouseleave',()=>this._clearHoveredHotspot(hs));
        hs.addEventListener('focus',()=>this._setHoveredHotspot(hs));
        hs.addEventListener('blur',()=>this._clearHoveredHotspot(hs));
        hs.addEventListener('click',()=>{
          if (hs.getAttribute('aria-expanded')==='true'){this._closeLore();return;}
          if (this.panel.isOpen()) this.panel.close();
          if (this._openLore(hs)) {
            this.audio?.playSFX('hotspot');
          }
        });
      });
    }

    _bindBack() {
      this.back?.addEventListener('click',e=>{
        const href=this.back.getAttribute('href');
        if (!href||href==='#') return;
        e.preventDefault();
        this._closeLore();
        this.audio?.playSFX('navigate');
        this._navigate(href);
      });
    }

    _bindCTA() {
      this.cta?.addEventListener('click',e=>{
        this._hoveredHotspot = null;
        this._closeLore();
        const mode = this.cta?.dataset.ctaMode || 'panel';
        if (mode !== 'redirect') return;
        const href = this.cta.getAttribute('href');
        if (!href || href === '#') return;
        e.preventDefault();
        this.audio?.playSFX('navigate');
        this._navigate(href);
      });
    }

    _navigate(href) {
      if (this.atm) this.atm.setMode('active');
      if (window.NoxPageTransition?.navigate) {
        window.NoxPageTransition.navigate(href, {
          source: this.stage || this.el,
          duration: this.transition.duration,
          intensity: this.transition.intensity,
          chaos: this.transition.chaos,
          blur: this.transition.blur
        });
        return;
      }
      window.location.href = href;
    }

    _bindAudioBtn() {
      if (!this.audioBtn||!this.audio) return;
      const on=this.audioBtn.querySelector('.nox-audio-icon--on');
      const off=this.audioBtn.querySelector('.nox-audio-icon--off');
      this.audioBtn.addEventListener('click',()=>{
        const muted=this.audio.toggle();
        this.audioBtn.setAttribute('aria-pressed',String(muted));
        if (on)  on.style.display =muted?'none':'';
        if (off) off.style.display=muted?'':'none';
      });
    }

    _bindKeyboard() {
      this._onKeydown = e=>{
        if (e.key!=='Escape') return;
        if (this.lore.isOpen()){this._closeLore();return;}
        if (this.panel.isOpen()){this.panel.close();return;}
      };
      document.addEventListener('keydown', this._onKeydown);
    }

    _bindDismiss() {
      this._onDocumentClick = e => {
        if (!(e.target instanceof Element)) return;
        if (!this.lore.isOpen()) return;
        if (e.target.closest('.nox-hotspot')) return;
        this._closeLore();
      };
      document.addEventListener('click', this._onDocumentClick);
    }

    _bindResize() {
      this._onResize = () => {
        this._layoutStage();
        this.lore.refreshPosition();
      };
      window.addEventListener('resize', this._onResize);
    }

    destroy() {
      this._hoveredHotspot = null;
      this._closeLore();
      this.el.classList.remove('is-hotspot-hovered','has-active-hotspot');
      if (this._onKeydown) {
        document.removeEventListener('keydown', this._onKeydown);
        this._onKeydown = null;
      }
      if (this._onDocumentClick) {
        document.removeEventListener('click', this._onDocumentClick);
        this._onDocumentClick = null;
      }
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        this._onResize = null;
      }
      if (this._onPointerMove) {
        this.el.removeEventListener('pointermove', this._onPointerMove);
        this._onPointerMove = null;
      }
      if (this._onPointerLeave) {
        this.el.removeEventListener('pointerleave', this._onPointerLeave);
        this._onPointerLeave = null;
      }
      if (this._parallaxFrame) {
        window.cancelAnimationFrame(this._parallaxFrame);
        this._parallaxFrame = 0;
      }
      this.el.style.setProperty('--parallax-x', '0px');
      this.el.style.setProperty('--parallax-y', '0px');
      if (this.atm) {
        this.atm.destroy();
        this.atm = null;
      }
    }
  }

  let activeRoom = null;
  function mount(el) {
    if (activeRoom) activeRoom.destroy();
    activeRoom = new Room(el);
  }
  function init() { const el=document.querySelector('.nox-room'); if(el) mount(el); }
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',init);
  else init();
  document.addEventListener('shopify:section:load',e=>{
    const el=e.target.querySelector('.nox-room');
    if(el) mount(el);
  });
  document.addEventListener('shopify:section:unload',e=>{
    if (!activeRoom) return;
    const el = e.target.querySelector('.nox-room');
    if (el && activeRoom.el === el) {
      activeRoom.destroy();
      activeRoom = null;
    }
  });
})();

/* ==========================================
   NOXCULT ROOM — JS ANIMATION ENGINE
   ==========================================
   Colle dans un <script> en bas de ta page Room,
   ou dans un fichier .js chargé en defer.
   ========================================== */
 
(function() {
  'use strict';
 
  /* CONFIG — ajuste ici si besoin */
  var WIPE_STAGGER  = 50;    /* délai entre chaque bande (ms) */
  var WIPE_DURATION = 350;   /* durée de chaque bande (ms) */
  var WIPE_PAUSE    = 100;   /* pause au noir entre in/out (ms) */
  var LINE_STAGGER  = 150;   /* délai entre lignes parallax (ms) */
  var COOLDOWN      = 1200;  /* cooldown entre deux transitions (ms) */
  var TOUCH_MIN     = 50;    /* distance min swipe (px) */
 
  /* STATE */
  var sections = document.querySelectorAll('.room-section');
  var total = sections.length;
  var current = 0;
  var locked = false;
  var lastWheel = 0;
  var touchY = 0;
 
  /* ==============================
     NAV DOTS — générés auto
     ============================== */
  var dotsEl = document.getElementById('roomDots');
  if (dotsEl) {
    for (var i = 0; i < total; i++) {
      var dot = document.createElement('button');
      dot.className = 'room-dots__dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Section ' + (i + 1));
      (function(idx) {
        dot.addEventListener('click', function() { goTo(idx); });
      })(i);
      dotsEl.appendChild(dot);
    }
  }
 
  function updateDots(idx) {
    if (!dotsEl) return;
    var dots = dotsEl.querySelectorAll('.room-dots__dot');
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('is-active', i === idx);
    }
  }
 
  /* ==============================
     REVEAL / HIDE
     ============================== */
  function reveal(idx) {
    var sec = sections[idx];
    sec.classList.add('is-current');
    requestAnimationFrame(function() {
      var c = sec.querySelector('.room-content');
      if (c) c.classList.add('is-visible');
 
      var lines = sec.querySelectorAll('.room-parallax__line');
      for (var i = 0; i < lines.length; i++) {
        (function(line, d) {
          setTimeout(function() { line.classList.add('is-visible'); }, d);
        })(lines[i], i * LINE_STAGGER);
      }
    });
  }
 
  function hide(idx) {
    var sec = sections[idx];
    var c = sec.querySelector('.room-content');
    if (c) {
      c.classList.remove('is-visible');
      c.style.transition = 'none';
      c.style.opacity = '0';
      c.style.transform = 'translateY(40px)';
      requestAnimationFrame(function() {
        c.style.removeProperty('transition');
        c.style.removeProperty('opacity');
        c.style.removeProperty('transform');
      });
    }
 
    var lines = sec.querySelectorAll('.room-parallax__line');
    for (var i = 0; i < lines.length; i++) {
      lines[i].classList.remove('is-visible');
    }
 
    sec.classList.remove('is-current');
  }
 
  /* ==============================
     WIPE TRANSITION
     ============================== */
  function wipe(from, to) {
    if (locked || from === to) return;
    locked = true;
 
    var strips = document.querySelectorAll('.room-wipe__strip');
    var down = to > from;
    var originIn = down ? 'top' : 'bottom';
    var originOut = down ? 'bottom' : 'top';
    var ease = 'var(--nox-ease)';
 
    /* Reset */
    for (var i = 0; i < strips.length; i++) {
      strips[i].style.transformOrigin = originIn;
      strips[i].style.transform = 'scaleY(0)';
      strips[i].style.transition = 'none';
    }
 
    /* IN */
    for (var i = 0; i < strips.length; i++) {
      (function(s, d) {
        setTimeout(function() {
          s.style.transition = 'transform ' + WIPE_DURATION + 'ms ' + ease;
          s.style.transform = 'scaleY(1)';
        }, d);
      })(strips[i], i * WIPE_STAGGER);
    }
 
    var swapAt = WIPE_DURATION + strips.length * WIPE_STAGGER;
 
    /* SWAP pendant le noir */
    setTimeout(function() {
      hide(from);
      current = to;
      updateDots(to);
    }, swapAt - 50);
 
    /* OUT */
    setTimeout(function() {
      for (var i = 0; i < strips.length; i++) {
        strips[i].style.transformOrigin = originOut;
      }
      for (var i = 0; i < strips.length; i++) {
        (function(s, d) {
          setTimeout(function() {
            s.style.transition = 'transform ' + WIPE_DURATION + 'ms ' + ease;
            s.style.transform = 'scaleY(0)';
          }, d);
        })(strips[i], i * WIPE_STAGGER);
      }
    }, swapAt + WIPE_PAUSE);
 
    /* REVEAL */
    setTimeout(function() {
      reveal(to);
    }, swapAt + WIPE_PAUSE + 80);
 
    /* UNLOCK */
    setTimeout(function() {
      locked = false;
    }, swapAt + WIPE_PAUSE + WIPE_DURATION + strips.length * WIPE_STAGGER + 100);
  }
 
  function goTo(idx) { wipe(current, idx); }
 
  /* ==============================
     INTRO (optionnel)
     ============================== */
  function runIntro() {
    var intro = document.getElementById('roomIntro');
    if (!intro) { reveal(0); return; }
 
    var l1 = document.getElementById('introL1');
    var l2 = document.getElementById('introL2');
    var l3 = document.getElementById('introL3');
    var txt = document.getElementById('introText');
    var ease = 'var(--nox-ease)';
 
    setTimeout(function() { txt.style.transition = 'opacity 0.6s ease'; txt.style.opacity = '1'; }, 300);
    setTimeout(function() { l3.style.transition = 'transform 0.7s ' + ease; l3.style.transform = 'translateY(0)'; }, 200);
    setTimeout(function() { l2.style.transition = 'transform 0.7s ' + ease; l2.style.transform = 'translateY(0)'; }, 500);
    setTimeout(function() { txt.style.opacity = '0'; }, 1400);
    setTimeout(function() {
      l1.style.transition = 'transform 0.8s ' + ease;
      l2.style.transition = 'transform 0.8s ' + ease;
      l3.style.transition = 'transform 0.8s ' + ease;
      l1.style.transform = 'translateY(-100%)';
      l2.style.transform = 'translateY(-100%)';
      l3.style.transform = 'translateY(-100%)';
    }, 1800);
    setTimeout(function() { intro.style.display = 'none'; reveal(0); }, 2700);
  }
 
  /* ==============================
     EVENTS
     ============================== */
  window.addEventListener('wheel', function(e) {
    e.preventDefault();
    var now = Date.now();
    if (now - lastWheel < COOLDOWN) return;
    lastWheel = now;
    if (e.deltaY > 0 && current < total - 1) goTo(current + 1);
    else if (e.deltaY < 0 && current > 0) goTo(current - 1);
  }, { passive: false });
 
  window.addEventListener('touchstart', function(e) { touchY = e.touches[0].clientY; }, { passive: true });
  window.addEventListener('touchend', function(e) {
    var diff = touchY - e.changedTouches[0].clientY;
    if (Math.abs(diff) < TOUCH_MIN) return;
    if (diff > 0 && current < total - 1) goTo(current + 1);
    else if (diff < 0 && current > 0) goTo(current - 1);
  }, { passive: true });
 
  document.addEventListener('keydown', function(e) {
    if (e.key === 'ArrowDown' && current < total - 1) goTo(current + 1);
    if (e.key === 'ArrowUp' && current > 0) goTo(current - 1);
  });
 
  /* ==============================
     INIT
     ============================== */
  runIntro();
 
})();
